/* ez-cart-drawer.js — drives the slide-over cart drawer (snippets/ez-cart-drawer.liquid,
   rendered once from layout/theme.liquid).

   Responsibilities:
   - Fetch /cart.js on open and after any add-to-cart (islands dispatch `ez:cart:added`).
   - Render line rows from the server-authored <template>, wire qty +/- , remove, subtotal.
   - Open/close with focus-trap, ESC, backdrop click. Reduced-motion aware (CSS handles it).

   Line mutations go through /cart/change.js (by line index) and re-render from the response,
   so quantities/subtotal always reflect Shopify's authoritative cart. No optimistic drift. */
(function () {
  'use strict';

  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),[tabindex]:not([tabindex="-1"])';
  var drawer, panel, linesEl, tpl, packTpl, aggTpl, emptyEl, loadingEl, footEl, subtotalEl, countEl, bodyEl, upsellEl;
  var SNAP_COUNTS = (window.__EZ_SNAP_COUNTS__) || { skt: {}, shp: {}, hit: 100 };
  var COLOR_NAME = { BK: 'Black', BG: 'Beige', WH: 'White', GY: 'Grey' };
  var pinNote = ''; // pin ship-time note from the drawer root's data-ez-pin-note ('' = off)
  var lastFocus = null;
  var loaded = false;

  function money(cents) {
    // Non-store currency (Canada market = CAD): the ONE theme formatter, window.EZ (layout/theme.liquid).
    // /cart.js amounts are already in the presentment currency; Shopify.formatMoney is not defined on this
    // theme, so without this a CAD cart fell through to the bare "$" + toFixed(2) USD render below.
    var E = window.EZ;
    if (E && E.isBase === false && typeof E.money === 'function') return E.money((Number(cents) || 0) / 100);
    // Prefer Shopify's storefront currency format if present, else a plain USD render.
    if (window.Shopify && window.Shopify.formatMoney) {
      var fmt = (window.theme && window.theme.moneyFormat) || '${{amount}}';
      return window.Shopify.formatMoney(cents, fmt);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function q(sel, root) { return (root || document).querySelector(sel); }

  function cache() {
    drawer = q('[data-ez-cart-drawer]');
    if (!drawer) return false;
    panel = q('[data-ez-cart-panel]', drawer);
    bodyEl = q('[data-ez-cart-body]', drawer);
    linesEl = q('[data-ez-cart-lines]', drawer);
    tpl = q('[data-ez-cart-row-tpl]', drawer);
    packTpl = q('[data-ez-cart-pack-tpl]', drawer);
    aggTpl = q('[data-ez-cart-agg-tpl]', drawer);
    emptyEl = q('[data-ez-cart-empty]', drawer);
    loadingEl = q('[data-ez-cart-loading]', drawer);
    footEl = q('[data-ez-cart-foot]', drawer);
    subtotalEl = q('[data-ez-cart-subtotal]', drawer);
    countEl = q('[data-ez-cart-count]', drawer);
    upsellEl = q('[data-ez-cd-upsell]', drawer);
    pinNote = (drawer.getAttribute('data-ez-pin-note') || '').trim();
    return true;
  }

  /* ---------- classification (SKU-family scheme; see the configurator engines) ----------
     Two DISTINCT separate-line families, never summed:
       SNA "Snap Pack"        = full fastener (caps+pins+studs), MESH-colour, the shade ROLL's required
                                coverage (kits bundle their own snaps in-kit — never a separate line).
       SNS "Body-Color Stud"  = studs only, OFF/body-colour, an OPTIONAL add-on (skirting/shade/hitch).
     Kits carry no snap count here — the in-kit snaps live inside the kit line, not in any aggregate. */
  // read a line-item property (/cart.js gives an object; be defensive about array form too)
  function propVal(item, key) {
    var p = item.properties;
    if (!p) return '';
    if (Array.isArray(p)) { for (var i = 0; i < p.length; i++) { if (p[i] && p[i][0] === key) return p[i][1] || ''; } return ''; }
    return p[key] || '';
  }

  function classify(item) {
    var sku = item.sku || '', m;
    // inclSnaps = the matching-colour snaps bundled IN the kit (shown on the kit's "includes" line, not a box)
    if ((m = /EZ-B-SKT-(\d+)-/.exec(sku))) return { role: 'kit', family: 'skirt', inclSnaps: SNAP_COUNTS.skt[m[1]] || 0 };
    if ((m = /EZ-B-SKS-(\d+)-/.exec(sku))) return { role: 'kit', family: 'skirt', inclSnaps: (SNAP_COUNTS.sks || {})[m[1]] || 0 };
    if (/EZ-B-HIT/.test(sku)) return { role: 'kit', family: 'hitch', inclSnaps: SNAP_COUNTS.hit || 0 };
    if ((m = /EZ-B-SHP-(\d+x\d+)/i.exec(sku))) return { role: 'kit', family: 'shade', inclSnaps: SNAP_COUNTS.shp[m[1].toLowerCase()] || 0 };
    if (/EZ-C-SHR/.test(sku)) return { role: 'kit', family: 'shade', inclSnaps: 0 }; // roll ships no snaps → coverage is the SNA box
    // a kit's own snap packs (stud color swap): $0 included; a different stud color's $60 rides on one pack line,
    // shown as its own "Stud color upgrade" box inside the build
    if ((m = /^EZ-C-SNA-(\d+)-([A-Z]{2})-([A-Z]{2})$/.exec(sku)) && propVal(item, '_kit_snaps') === '1') {
      return { role: 'pack', group: 'kitsnaps', count: +m[1], cap: m[2], stud: m[3], upgrade: false };
    }
    if ((m = /EZ-C-SNA-(\d+)-/.exec(sku))) return { role: 'pack', group: 'snaps', count: +m[1] };
    if ((m = /EZ-C-SNS-(\d+)-/.exec(sku))) return { role: 'pack', group: 'studs', count: +m[1] };
    // Pipe-clamp pack (skirting/hitch anchoring add-on): compact nested pack, same treatment as SNA/SNS;
    // never a full product row. SKU is EZ-B-CMP-<packSize> (no trailing colour; stainless has no colour).
    if ((m = /EZ-B-CMP-(\d+)/.exec(sku))) return { role: 'pack', group: 'clamps', count: +m[1] };
    return { role: 'other' };
  }

  // Does this line ship snap pins (→ the pin ship-time note)? Kits / precuts (EZ-B-SKT/SKS/HIT/SHP), snap and pin
  // packs (EZ-C-SNA/SNP) and samples (SAMPLES-) do; studs, caps, screws, rolls, clamps, bags, velcro and tools don't.
  // productType is the fallback for a blank or renamed SKU. Mirrors snippets/ez-cart-line-row.liquid (pin_note).
  function isPinSku(sku, productType) {
    var s = String(sku || '').trim().toUpperCase().replace(/^W-/, '');
    if (/^EZ-B-(SKT|SKS|HIT|SHP)-/.test(s) || /^EZ-C-(SNA|SNP)-/.test(s) || /^SAMPLES-/.test(s)) return true;
    var t = String(productType || '').trim();
    return t === 'RV Skirting Kit' || t === 'Hitch Enclosure Kit' || t === 'Precut Window Shade';
  }

  // Fill the note's text span, keeping the alert icon beside it (fallback: plain text if the span is missing).
  function setShipText(el) {
    var t = q('[data-ez-ship-text]', el);
    if (t) t.textContent = pinNote; else el.textContent = pinNote;
  }

  function packColor(item) {
    var opts = (item.options_with_values || []).map(function (o) { return o.value; });
    for (var i = 0; i < opts.length; i++) { if (/^(black|beige|white|grey|gray)$/i.test(opts[i])) return opts[i]; }
    var m = /-([A-Z]{2})$/.exec(item.sku || '');
    return m ? (COLOR_NAME[m[1]] || '') : '';
  }

  /* ---------- builders ---------- */

  function buildRow(item, line, priceOverride) {
    var node = tpl.content.firstElementChild.cloneNode(true);
    node.setAttribute('data-line', String(line));
    node.setAttribute('data-key', item.key);
    [q('[data-ez-line-url]', node), q('[data-ez-line-title-link]', node)].forEach(function (a) { if (a) a.setAttribute('href', item.url); });
    var img = q('[data-ez-line-img]', node);
    if (img) {
      // Prefer the per-build image the shade builder stamps (_build_img, absolute URL) so each build shows its
      // own shot; fall back to the resolved product's featured image. The resize regex is a no-op on any URL
      // whose extension it can't find, so it's safe on either source.
      var lineImg = propVal(item, '_build_img') || item.image;
      if (lineImg) { img.src = lineImg.replace(/(\.[a-z]+)(\?|$)/i, '_120x120$1$2'); img.alt = item.product_title || ''; }
      else { img.remove(); }
    }
    var titleLink = q('[data-ez-line-title-link]', node);
    if (titleLink) titleLink.textContent = item.product_title || item.title;
    var variantEl = q('[data-ez-line-variant]', node);
    if (variantEl) {
      var opts = (item.options_with_values || [])
        .filter(function (o) { return o.value && o.value.toLowerCase() !== 'default title'; })
        .map(function (o) { return o.value; });
      if (opts.length) variantEl.textContent = opts.join(' / '); else variantEl.remove();
    }
    var inclEl = q('[data-ez-line-incl]', node);
    if (inclEl) {
      var kcls = classify(item);
      if (kcls.role === 'kit' && kcls.inclSnaps > 0) inclEl.textContent = '✓ Includes ' + kcls.inclSnaps + ' snaps';
      else inclEl.remove();
    }
    var shipEl = q('[data-ez-line-ship]', node);
    if (shipEl) {
      if (pinNote && isPinSku(item.sku, item.product_type)) setShipText(shipEl);
      else shipEl.remove();
    }
    var qtyInput = q('[data-ez-line-qty]', node);
    if (qtyInput) qtyInput.value = item.quantity;
    var priceEl = q('[data-ez-line-price]', node);
    if (priceEl) priceEl.textContent = money(priceOverride != null ? priceOverride : item.final_line_price);
    return node;
  }

  function buildPackRow(entry) {
    var item = entry.item, node = packTpl.content.firstElementChild.cloneNode(true);
    node.setAttribute('data-line', String(entry.line));
    node.setAttribute('data-key', item.key);
    var titleEl = q('[data-ez-pack-title]', node);
    if (titleEl) titleEl.textContent = item.product_title || item.title || (entry.cls.count + '-ct ' + entry.cls.noun);
    var metaEl = q('[data-ez-pack-meta]', node);
    if (metaEl) {
      var vt = (item.variant_title && item.variant_title.toLowerCase() !== 'default title') ? item.variant_title : '';
      metaEl.textContent = (vt ? vt + ' · ' : '') + (item.sku || '');
    }
    var priceEl = q('[data-ez-pack-price]', node);
    if (priceEl) {
      priceEl.textContent = money(item.final_line_price);
      if (item.quantity > 1) {
        var ea = document.createElement('span');
        ea.className = 'ez-cart-pack__price-ea';
        ea.textContent = money(item.final_price) + ' ea';
        priceEl.appendChild(ea);
      }
    }
    var qi = q('[data-ez-line-qty]', node);
    if (qi) qi.value = item.quantity;
    return node;
  }

  function buildAgg(group, a) {
    var node = aggTpl.content.firstElementChild.cloneNode(true);
    if (group === 'kitsnaps' || group === 'upgrade') {
      var kn = aggTpl.content.firstElementChild.cloneNode(true);
      var kship = q('[data-ez-agg-ship]', kn);
      if (kship) kship.remove(); // kit-owned packs: the kit row already carries the pin ship-time note
      var studName = COLOR_NAME[a.stud] || '';
      var capName = COLOR_NAME[a.cap] || '';
      q('[data-ez-agg-label]', kn).textContent = group === 'upgrade' ? 'Stud color upgrade' : 'Snap packs';
      q('[data-ez-agg-total]', kn).textContent = group === 'upgrade'
        ? (studName + ' studs')
        : (a.count + (a.count === 1 ? ' snap' : ' snaps') + (capName && studName
            ? ' · ' + (capName === studName ? capName + ' caps & studs' : capName + ' caps, ' + studName + ' studs') : ''));
      q('[data-ez-agg-break]', kn).textContent = group === 'upgrade' ? 'one flat charge for this kit'
        : (a.price > 0 ? 'sized to your kit' : 'included with your kit');
      var kp = q('[data-ez-agg-price]', kn);
      if (kp) kp.textContent = money(a.price);
      if (group === 'upgrade') {
        // a flat line, nothing to expand
        var kc = q('.ez-cart-agg__chev', kn), kb = q('[data-ez-agg-body]', kn), kh = q('[data-ez-agg-toggle]', kn);
        if (kc) kc.remove();
        if (kb) kb.remove();
        if (kh) { kh.removeAttribute('data-ez-agg-toggle'); kh.removeAttribute('aria-expanded'); kh.disabled = true; kh.style.cursor = 'default'; }
        return kn;
      }
      // the kit's own packs are sized to the kit: list them, but no quantity, remove or per-pack price
      var kul = q('[data-ez-agg-packs]', kn);
      a.packs.forEach(function (e) {
        var row = buildPackRow(e);
        ['.ez-cart-pack__qty', '.ez-cart-pack__remove', '[data-ez-pack-price]'].forEach(function (sel) {
          var el = q(sel, row);
          if (el) el.remove();
        });
        var t = q('[data-ez-pack-title]', row);
        if (t) t.textContent = e.cls.count + ' snaps' + (e.item.quantity > 1 ? ' ×' + e.item.quantity : '') + ' · ' + (COLOR_NAME[e.cls.cap] || '') + ' caps, ' + (COLOR_NAME[e.cls.stud] || '') + ' studs';
        kul.appendChild(row);
      });
      var kno = q('.ez-cart-agg__note', kn);
      if (kno) kno.textContent = 'Sized to your kit and shipped with it. Caps match your material color.';
      return kn;
    }
    var isStuds = group === 'studs';
    var isClamps = group === 'clamps';
    var noun = isStuds ? ' studs' : isClamps ? (a.count === 1 ? ' clamp' : ' clamps') : (a.count === 1 ? ' snap' : ' snaps');
    var clr = (a.color && !isClamps) ? ' (' + a.color + ')' : ''; // clamps are stainless, no colour
    q('[data-ez-agg-label]', node).textContent = isStuds ? 'Body-color studs' : isClamps ? 'Pipe clamps' : 'Snaps';
    q('[data-ez-agg-total]', node).textContent = a.count + noun + clr;
    // snaps = the roll's required mesh coverage; studs = a body-colour upgrade; clamps = a PVC-frame anchor
    q('[data-ez-agg-break]', node).textContent = (isStuds || isClamps) ? 'optional add-on' : 'recommended for your build';
    // retail snap packs carry pins → the pin ship-time note; studs and clamps don't
    var shipEl = q('[data-ez-agg-ship]', node);
    if (shipEl) {
      if (group === 'snaps' && pinNote) setShipText(shipEl);
      else shipEl.remove();
    }
    var priceEl = q('[data-ez-agg-price]', node);
    if (priceEl) priceEl.textContent = money(a.price);
    var packsUl = q('[data-ez-agg-packs]', node);
    a.packs.forEach(function (e) { packsUl.appendChild(buildPackRow(e)); });
    return node;
  }

  /* ---------- rendering ---------- */

  // Build-aware count: each configured build (hidden _build prop) = 1; standalone lines count by qty.
  // Mirrors snippets/ez-cart-count.liquid so the drawer heading matches the grouped build sections
  // (raw cart.item_count summed the hidden snap/stud pack lines, showing e.g. "6 items" for 2 builds).
  function ezBuildCount(cart) {
    var seen = {}, n = 0;
    (cart.items || []).forEach(function (it) {
      var bid = propVal(it, '_build') || '';
      if (!bid) { n += it.quantity || 1; }
      else if (!seen[bid]) { seen[bid] = 1; n += 1; }
    });
    return n;
  }

  // Add-on row (theme settings → Cart drawer add-ons): visible only when the cart holds a skirting/side/hitch
  // kit; an add-on already in the cart hides; the whole row hides when nothing is left to offer.
  function syncUpsell(cart) {
    if (!upsellEl) return;
    var items = (cart && cart.items) || [];
    var hasKit = items.some(function (it) { return /^(W-)?EZ-B-(SKT|SKS|HIT)-/.test(it.sku || ''); });
    var inCart = {};
    items.forEach(function (it) { inCart[String(it.product_id)] = true; });
    var left = 0;
    Array.prototype.forEach.call(upsellEl.querySelectorAll('[data-ez-cd-up-item]'), function (li) {
      var hide = !!inCart[li.getAttribute('data-product-id')];
      li.hidden = hide;
      if (!hide) left += 1;
    });
    upsellEl.hidden = !(hasKit && left > 0);
  }

  function addUpsell(btn) {
    var id = parseInt(btn.getAttribute('data-variant-id'), 10);
    if (!id) return;
    btn.disabled = true;
    var label = btn.textContent;
    btn.textContent = 'Adding…';
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: id, quantity: 1 }] })
    })
      .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
      .then(function () { return fetchCart(); })
      .then(function (cart) {
        if (cart) document.dispatchEvent(new CustomEvent('ez:cart:updated', { detail: { cart: cart } }));
      })
      .catch(function () { btn.textContent = 'Try again'; })
      .then(function () { btn.disabled = false; if (btn.textContent === 'Adding…') btn.textContent = label; });
  }

  function render(cart) {
    loaded = true;
    syncUpsell(cart);
    if (loadingEl) loadingEl.hidden = true;

    var rawCount = cart.item_count || 0;
    var count = ezBuildCount(cart);
    if (countEl) countEl.textContent = count === 1 ? '1 item' : count + ' items';

    if (!rawCount) {
      if (linesEl) { linesEl.hidden = true; linesEl.innerHTML = ''; }
      if (footEl) footEl.hidden = true;
      if (emptyEl) emptyEl.hidden = false;
      return;
    }

    if (emptyEl) emptyEl.hidden = true;
    if (footEl) footEl.hidden = false;
    if (linesEl) { linesEl.hidden = false; linesEl.innerHTML = ''; }

    // website SKUs carry a "W-" channel prefix (ShipStation/NetSuite); every parser below reads the normalized SKU
    cart.items.forEach(function (it) { if (it.sku) it.sku = String(it.sku).replace(/^W-/, ''); });
    var items = cart.items.map(function (item, i) { return { item: item, line: i + 1, cls: classify(item), bid: propVal(item, '_build') }; });

    // Group lines into per-BUILD sections (via the hidden _build property set on ATC). Each build renders a
    // header + its kit/panel rows + its own Snaps (SNA coverage) / Body-color-studs (SNS) boxes. Untagged
    // lines (old carts / direct adds) fall into a final unlabelled group so nothing is lost.
    var order = [];
    items.forEach(function (e) { if (e.bid && order.indexOf(e.bid) < 0) order.push(e.bid); });
    if (items.some(function (e) { return !e.bid; })) order.push('');
    var groups = order.map(function (bid) {
      var g = items.filter(function (e) { return e.bid === bid; });
      var label = ''; g.forEach(function (e) { if (!label) label = propVal(e.item, '_build_label'); });
      return { bid: bid, items: g, label: label };
    });
    // number same-label builds so two "RV shade" builds read #1 / #2 instead of identical headers
    var labelCounts = {}, labelSeen = {};
    groups.forEach(function (gr) { if (gr.label) labelCounts[gr.label] = (labelCounts[gr.label] || 0) + 1; });

    groups.forEach(function (gr) {
      var group = gr.items;
      var label = gr.label;
      if (label) {
        var disp = 'Your ' + label + ' build';
        if (labelCounts[label] > 1) { labelSeen[label] = (labelSeen[label] || 0) + 1; disp += ' #' + labelSeen[label]; }
        var head = document.createElement('li');
        head.className = 'ez-cart-build-head';
        var headTitle = document.createElement('span');
        headTitle.className = 'ez-cart-build-head__title';
        headTitle.textContent = disp;
        head.appendChild(headTitle);
        // "Remove build" — carries every line key in this build so one click clears the kit + its packs.
        var headRemove = document.createElement('button');
        headRemove.type = 'button';
        headRemove.className = 'ez-cart-build-head__remove';
        headRemove.setAttribute('data-ez-remove-build', '');
        headRemove.setAttribute('data-keys', group.map(function (e) { return e.item.key; }).join(','));
        headRemove.setAttribute('data-label', label);
        headRemove.setAttribute('aria-label', 'Remove your ' + label + ' build and everything in it');
        headRemove.textContent = 'Remove build';
        head.appendChild(headRemove);
        linesEl.appendChild(head);
      }
      var agg = { snaps: { count: 0, price: 0, packs: [], color: '' }, studs: { count: 0, price: 0, packs: [], color: '' }, clamps: { count: 0, price: 0, packs: [], color: '' },
        kitsnaps: { count: 0, price: 0, packs: [], stud: '', cap: '' }, upgrade: { count: 0, price: 0, packs: [], stud: '' } };
      // kit snap packs left behind after their kit was removed are ordinary (retail, editable) snap packs
      var hasKit = group.some(function (e) { return /^EZ-B-(SKT|SKS|HIT|SHP)-/.test(e.item.sku || ''); });
      if (!hasKit) group.forEach(function (e) { if (e.cls.group === 'kitsnaps') e.cls = { role: 'pack', group: 'snaps', count: e.cls.count }; });
      // Did the cart transform price this build's kit packs? Then every pack is $0 except at most one mixed-color
      // pack carrying the flat upgrade ($60 at the cart's currency rate). Otherwise (transform off, a guard rail
      // tripped) they show at their real price under Snap packs and there is no upgrade row.
      var kitPacks = group.filter(function (e) { return e.cls.group === 'kitsnaps'; });
      var priced = kitPacks.filter(function (e) { return (e.item.final_line_price || 0) > 0; });
      var upCents = Math.round(6000 * ((window.Shopify && window.Shopify.currency && +window.Shopify.currency.rate) || 1));
      var repriced = priced.length === 0 || (priced.length === 1 && priced[0].cls.cap !== priced[0].cls.stud &&
        priced[0].item.final_line_price <= upCents + priced[0].item.quantity);
      if (repriced && priced.length === 1) priced[0].cls.upgrade = true;
      // Function v6: every kit pack $0 and the flat upgrade on the kit line (skirting > side kit > hitch, qty 1 first)
      var upOnKit = null, mixedPack = null;
      kitPacks.forEach(function (e) { if (e.cls.cap !== e.cls.stud) mixedPack = e; });
      if (repriced && priced.length === 0 && mixedPack) {
        var famOrder = { SKT: 0, SKS: 1, HIT: 2 };
        upOnKit = group.filter(function (e) { return /^EZ-B-(SKT|SKS|HIT)-/.test(e.item.sku || ''); })
          .sort(function (a, b) {
            return ((a.item.quantity === 1 ? 0 : 1) - (b.item.quantity === 1 ? 0 : 1)) ||
              (famOrder[(a.item.sku || '').split('-')[2]] - famOrder[(b.item.sku || '').split('-')[2]]);
          })[0] || null;
        if (upOnKit && (upOnKit.item.final_line_price || 0) >= upCents) upOnKit.priceOverride = upOnKit.item.final_line_price - upCents;
        else upOnKit = null;
      }
      group.forEach(function (e) {
        var c = e.cls;
        if (c.role === 'pack' && c.group === 'kitsnaps') {
          agg.kitsnaps.count += c.count * e.item.quantity;
          agg.kitsnaps.packs.push(e);
          if (!agg.kitsnaps.stud) { agg.kitsnaps.stud = c.stud; agg.kitsnaps.cap = c.cap; }
          if (c.upgrade) { agg.upgrade.price += (e.item.final_line_price || 0); agg.upgrade.stud = c.stud; agg.upgrade.count = 1; }
          else agg.kitsnaps.price += (e.item.final_line_price || 0);
          return;
        }
        if (c.role === 'pack' && agg[c.group]) {
          var g = agg[c.group];
          g.count += c.count * e.item.quantity;
          g.price += (e.item.final_line_price || 0);
          g.packs.push(e);
          if (!g.color) g.color = packColor(e.item);
        }
      });
      group.forEach(function (e) { if (e.cls.role !== 'pack') linesEl.appendChild(buildRow(e.item, e.line, e.priceOverride)); });
      if (upOnKit) { agg.upgrade.price = upCents; agg.upgrade.stud = mixedPack.cls.stud; agg.upgrade.count = 1; }
      // Once-per-build "install kit" line for SHADE builds that ship snaps (precut bundle EZ-B-SHP or an added
      // EZ-C-SNA pack). The install consumables ride inside the snap pack, so a bare mesh-only roll shows none.
      var accShade = group.some(function (e) { return e.cls.family === 'shade'; });
      var accShips = group.some(function (e) { var sk = e.item.sku || ''; return /EZ-B-SHP/.test(sk) || /EZ-C-SNA/.test(sk); });
      if (accShade && accShips) {
        var accLi = document.createElement('li');
        accLi.className = 'ez-cart-build-incl';
        accLi.textContent = '✓ Primer pen, marking pencil & removal tool included';
        linesEl.appendChild(accLi);
      }
      if (agg.kitsnaps.packs.length) linesEl.appendChild(buildAgg('kitsnaps', agg.kitsnaps));
      if (agg.upgrade.count) linesEl.appendChild(buildAgg('upgrade', agg.upgrade));
      if (agg.snaps.packs.length) linesEl.appendChild(buildAgg('snaps', agg.snaps));
      if (agg.studs.packs.length) linesEl.appendChild(buildAgg('studs', agg.studs));
      if (agg.clamps.packs.length) linesEl.appendChild(buildAgg('clamps', agg.clamps));
    });

    if (subtotalEl) subtotalEl.textContent = money(cart.total_price);
  }

  /* ---------- data ---------- */

  function fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { render(cart); return cart; })
      .catch(function () {
        if (loadingEl) loadingEl.textContent = 'Could not load your cart.';
      });
  }

  function changeLine(line, quantity, rowEl) {
    if (rowEl) rowEl.classList.add('is-updating');
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        render(cart);
        document.dispatchEvent(new CustomEvent('ez:cart:updated', { detail: { cart: cart } }));
      })
      .catch(function () { if (rowEl) rowEl.classList.remove('is-updating'); });
  }

  // Batch-remove a set of line-item keys (Remove build) in one request, then re-render from the response.
  function updateCart(updates) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        render(cart);
        document.dispatchEvent(new CustomEvent('ez:cart:updated', { detail: { cart: cart } }));
        return cart;
      })
      .catch(function () { fetchCart(); });
  }

  // Empty the whole cart (Clear cart), then re-render (shows the empty state).
  function clearCart() {
    return fetch('/cart/clear.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        render(cart);
        document.dispatchEvent(new CustomEvent('ez:cart:updated', { detail: { cart: cart } }));
        return cart;
      })
      .catch(function () { fetchCart(); });
  }

  /* ---------- open / close + focus trap ---------- */

  function open() {
    if (!drawer) return;
    lastFocus = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    // Always refresh on open so the drawer reflects the live cart.
    fetchCart().then(function () {
      var target = q('[data-ez-cart-close]', panel) || panel;
      if (target && target.focus) target.focus();
    });
    document.addEventListener('keydown', onKeydown);
  }

  function close() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
    document.removeEventListener('keydown', onKeydown);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function onKeydown(e) {
    if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); close(); return; }
    if (e.key !== 'Tab') return;
    var nodes = Array.prototype.slice.call(panel.querySelectorAll(FOCUSABLE))
      .filter(function (n) { return n.offsetParent !== null; });
    if (!nodes.length) return;
    var first = nodes[0], last = nodes[nodes.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* ---------- events ---------- */

  function onDrawerClick(e) {
    var close2 = e.target.closest('[data-ez-cart-close]');
    if (close2) { e.preventDefault(); close(); return; }

    var upAdd = e.target.closest('[data-ez-cd-up-add]');
    if (upAdd) { e.preventDefault(); addUpsell(upAdd); return; }

    var aggToggle = e.target.closest('[data-ez-agg-toggle]');
    if (aggToggle) {
      var body = aggToggle.parentNode.querySelector('[data-ez-agg-body]');
      var isOpen = aggToggle.getAttribute('aria-expanded') === 'true';
      aggToggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      if (body) body.hidden = isOpen;
      return;
    }

    // "Remove build" — confirm, then zero every line key in this build in one request.
    var rmBuild = e.target.closest('[data-ez-remove-build]');
    if (rmBuild) {
      e.preventDefault();
      var bkeys = (rmBuild.getAttribute('data-keys') || '').split(',').filter(Boolean);
      if (!bkeys.length) return;
      var blabel = rmBuild.getAttribute('data-label') || 'this';
      window.ezConfirm({
        title: 'Remove this build?',
        message: 'This removes your ' + blabel + ' build and everything in it: the kit plus its snaps and studs.',
        confirmLabel: 'Remove build',
        danger: true
      }).then(function (ok) {
        if (!ok) return;
        var headEl = rmBuild.closest('.ez-cart-build-head');
        if (headEl) headEl.style.opacity = '0.5';
        var updates = {};
        bkeys.forEach(function (k) { updates[k] = 0; });
        updateCart(updates);
      });
      return;
    }

    // "Clear cart" — confirm, then empty everything.
    var clearBtn = e.target.closest('[data-ez-cart-clear]');
    if (clearBtn) {
      e.preventDefault();
      window.ezConfirm({
        title: 'Clear your cart?',
        message: 'This removes everything from your cart. This can’t be undone.',
        confirmLabel: 'Clear cart',
        danger: true
      }).then(function (ok) { if (ok) clearCart(); });
      return;
    }

    var row = e.target.closest('[data-ez-cart-line]');
    if (!row) return;
    var line = parseInt(row.getAttribute('data-line'), 10);

    if (e.target.closest('[data-ez-line-remove]')) {
      e.preventDefault();
      changeLine(line, 0, row);
      return;
    }
    if (e.target.closest('[data-ez-line-inc]')) {
      var incIn = q('[data-ez-line-qty]', row);
      changeLine(line, (parseInt(incIn.value, 10) || 0) + 1, row);
      return;
    }
    if (e.target.closest('[data-ez-line-dec]')) {
      var decIn = q('[data-ez-line-qty]', row);
      changeLine(line, Math.max(0, (parseInt(decIn.value, 10) || 0) - 1), row);
      return;
    }
  }

  function onDrawerChange(e) {
    var input = e.target.closest('[data-ez-line-qty]');
    if (!input) return;
    var row = input.closest('[data-ez-cart-line]');
    var line = parseInt(row.getAttribute('data-line'), 10);
    var qty = Math.max(0, parseInt(input.value, 10) || 0);
    changeLine(line, qty, row);
  }

  function init() {
    if (!cache()) return;

    drawer.addEventListener('click', onDrawerClick);
    drawer.addEventListener('change', onDrawerChange);

    // Any [data-ez-cart-open] on the page opens the drawer.
    document.addEventListener('click', function (e) {
      var opener = e.target.closest('[data-ez-cart-open]');
      if (!opener) return;
      e.preventDefault();
      open();
    });

    // Islands (PDP / configurators) dispatch this after a successful /cart/add.js.
    document.addEventListener('ez:cart:added', function () { open(); });

    // Public open hook.
    document.addEventListener('ez:cart:open', function () { open(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
